import axios from 'axios';
import { getApiUrl } from '../config/environment';

const API_URL = getApiUrl();

export interface VPSFirewallRule {
  id: number;
  vps_id: number;
  port: string;
  protocol: 'tcp' | 'udp' | 'both';
  direction: 'in' | 'out' | 'both';
  action: 'allow' | 'deny';
  source_ip?: string;
  description?: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface CreateFirewallRuleData {
  port: string;
  protocol: 'tcp' | 'udp' | 'both';
  direction: 'in' | 'out' | 'both';
  action: 'allow' | 'deny';
  source_ip?: string;
  description?: string;
  is_active?: boolean;
  priority?: number;
}

export interface UpdateFirewallRuleData {
  port?: string;
  protocol?: 'tcp' | 'udp' | 'both';
  direction?: 'in' | 'out' | 'both';
  action?: 'allow' | 'deny';
  source_ip?: string;
  description?: string;
  is_active?: boolean;
  priority?: number;
}

export interface FirewallStatus {
  enabled: boolean;
  status: string;
  rules_count: number;
  last_applied?: string;
}

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const getFirewallRules = async (vpsId: number): Promise<VPSFirewallRule[]> => {
  const response = await axios.get(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules`, getAuthHeader());
  return response.data.rules;
};

export const getActiveFirewallRules = async (vpsId: number): Promise<VPSFirewallRule[]> => {
  const response = await axios.get(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules/active`, getAuthHeader());
  return response.data.rules;
};

export const createFirewallRule = async (vpsId: number, data: CreateFirewallRuleData): Promise<VPSFirewallRule> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules`, data, getAuthHeader());
  return response.data.rule;
};

export const updateFirewallRule = async (vpsId: number, ruleId: number, data: UpdateFirewallRuleData): Promise<VPSFirewallRule> => {
  const response = await axios.put(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules/${ruleId}`, data, getAuthHeader());
  return response.data.rule;
};

export const deleteFirewallRule = async (vpsId: number, ruleId: number): Promise<void> => {
  await axios.delete(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules/${ruleId}`, getAuthHeader());
};

export const toggleFirewallRule = async (vpsId: number, ruleId: number): Promise<VPSFirewallRule> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/rules/${ruleId}/toggle`, {}, getAuthHeader());
  return response.data.rule;
};

export const applyFirewallRules = async (vpsId: number): Promise<{ message: string; applied_count: number }> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/apply`, {}, getAuthHeader());
  return response.data;
};

export const getFirewallStatus = async (vpsId: number): Promise<FirewallStatus> => {
  const response = await axios.get(`${API_URL}/api/admin/vps/${vpsId}/firewall/status`, getAuthHeader());
  return response.data.status;
};

export const enableFirewall = async (vpsId: number): Promise<{ message: string; status: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/enable`, {}, getAuthHeader());
  return response.data;
};

export const disableFirewall = async (vpsId: number): Promise<{ message: string; status: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/disable`, {}, getAuthHeader());
  return response.data;
};

export const resetFirewall = async (vpsId: number): Promise<{ message: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/reset`, {}, getAuthHeader());
  return response.data;
};

export const setupDefaultFirewall = async (vpsId: number): Promise<{ message: string; rules_created: number }> => {
  const response = await axios.post(`${API_URL}/api/admin/vps/${vpsId}/firewall/setup-default`, {}, getAuthHeader());
  return response.data;
};
